import React, { useEffect, useMemo } from "react";
import { enums } from "@shared/constants";
import { useColorMode } from "@shared/hooks/useColorMode";
import Box from "@ui-kit/layout/Box/Box.web";
import { loadStripe } from "@stripe/stripe-js";
import { convertPriceStringToInteger } from "@shared/modules/financials/utils/price-utils";
import {
  buildAffirmStyle,
  type StripeColorMode,
} from "@shared/modules/payments/utils/stripe/stripe-theme-utils";

export interface StripeAffirmTextProps {
  publishableApiKey: string;
  itemPrice: string;
  currency: typeof enums.CURRENCY_MAP.USD.key; // only "USD"
  isSoldOut: boolean;
  accessGrantedAccountId: string;
}

const StripeAffirmText: React.FC<StripeAffirmTextProps> = ({
  publishableApiKey,
  itemPrice,
  currency,
  isSoldOut,
  accessGrantedAccountId,
}) => {
  const domElementRef = React.useRef<HTMLDivElement>(null);
  const { activeTheme } = useColorMode();

  // The affirmMessage element is a legacy Stripe element that does not support
  // the appearance.theme API. Styling must go through element-level options.
  const affirmStyle = useMemo(
    () => buildAffirmStyle(activeTheme as StripeColorMode),
    [activeTheme]
  );

  useEffect(() => {
    let mountedElement: { unmount: () => void } | null = null;

    (async () => {
      if (!domElementRef.current) return;

      const stripe = await loadStripe(publishableApiKey, {
        ...(accessGrantedAccountId && {
          stripeAccount: accessGrantedAccountId,
        }),
      });
      // The only time stripe is nullish is on server environments.
      if (!stripe) return;

      const elements = stripe.elements();
      const affirmMessage = elements.create(
        // false positive error of TS expecting "issuingCardCopyButton"
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        "affirmMessage",
        {
          paymentMethodTypes: ["affirm"],
          amount: convertPriceStringToInteger(itemPrice),
          currency,
          ...affirmStyle,
        }
      );

      affirmMessage.mount(domElementRef.current);
      mountedElement = affirmMessage;
    })();

    return () => {
      mountedElement?.unmount();
    };
  }, [
    publishableApiKey,
    accessGrantedAccountId,
    itemPrice,
    currency,
    affirmStyle,
  ]);

  return (
    <Box
      data-testid="affirm-message-element"
      opacity={isSoldOut ? "0.3" : "1.0"}
      marginTop="xs"
    >
      <div ref={domElementRef} data-testid="stripe-element-affirm-messaging" />
    </Box>
  );
};

const arePropsEqual = (
  prevProps: StripeAffirmTextProps,
  newProps: StripeAffirmTextProps
) => {
  return (
    prevProps.publishableApiKey === newProps.publishableApiKey &&
    prevProps.itemPrice === newProps.itemPrice &&
    prevProps.currency === newProps.currency &&
    prevProps.isSoldOut === newProps.isSoldOut
  );
};

export default React.memo(StripeAffirmText, arePropsEqual);
